import { useEffect, useRef, useState } from "react";
import { usePanoramaProgress, useTourState, useTourStoreApi } from "@heroncendre/player";

/**
 * ============================================================================
 *  useChangementScene.ts — Aller à une scène par un fondu au noir
 * ----------------------------------------------------------------------------
 *  Depuis la navigation, on SAUTE d'un lieu à un autre, sans rapport de place
 *  entre les deux : le fondu enchaîné d'un hotspot (qui suggère un pas) ne
 *  convient pas. On passe donc par le noir de l'Écrin (`#101A17`) :
 *
 *    assombrir  l'écran s'assombrit (0,35 s ; 0,2 s en mouvement réduit) ;
 *    attendre   la scène change dans le noir, et l'on attend que son panorama
 *               soit affichable (`usePanoramaProgress`) — au plus 4 s ;
 *    clair      l'écran s'éclaircit.
 *
 *  Un second choix pendant le fondu remplace le premier : le dernier gagne.
 * ==========================================================================*/

const ATTENTE_MAX_MS = 4000;

type Phase = "clair" | "assombrir" | "attendre";

export function useChangementScene(reduit: boolean) {
  const store = useTourStoreApi();
  const progression = usePanoramaProgress();
  const apparitions = useTourState((s) => s.sceneReveals);
  const apparitionsAvant = useRef(0);
  const duree = reduit ? 200 : 350;
  const [phase, setPhase] = useState<Phase>("clair");
  const cible = useRef<string | null>(null);
  const minuteur = useRef(0);
  useEffect(() => () => window.clearTimeout(minuteur.current), []);

  /** Dans le noir : changer de scène, et ne pas attendre au-delà du raisonnable. */
  const changer = () => {
    const id = cible.current;
    apparitionsAvant.current = store.getState().sceneReveals;
    // Fondu du moteur à 0 : on est DANS le noir, le fondu est déjà fait. Avec celui du tour,
    // le moteur enchaînerait l'ancienne scène vers la nouvelle derrière l'écran noir, et
    // l'ancienne réapparaîtrait en s'effaçant dès que le noir se lève.
    if (id) store.getState().goToScene(id, undefined, 0);
    setPhase("attendre");
    window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(() => setPhase("clair"), ATTENTE_MAX_MS);
  };

  const aller = (sceneId: string) => {
    if (phase === "clair" && sceneId === store.getState().currentSceneId) return;
    cible.current = sceneId;
    if (phase === "attendre") return changer();
    if (phase === "assombrir") return; // le minuteur en cours prendra la dernière cible
    setPhase("assombrir");
    window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(changer, duree);
  };

  // La scène voulue est affichable (ou introuvable) ET le rendu la montre : on rend l'image.
  //
  // « Affichable » ne suffit pas. Le moteur n'installe pas la nouvelle scène à l'instant
  // où le store change : il attend, entre autres, que les points de l'ancienne aient fini
  // de s'effacer. Une scène déjà en cache est « prête » tout de suite — le noir se
  // levait alors sur l'ANCIENNE scène, encore à l'écran, qui s'effaçait ensuite. Le
  // compteur d'apparitions (`sceneReveals`) dit que le rendu a commencé à montrer la nouvelle.
  const prete =
    phase === "attendre" &&
    apparitions > apparitionsAvant.current &&
    cible.current !== null &&
    progression.sourceKey?.startsWith(`${cible.current}|`) === true &&
    progression.status !== "pending";
  useEffect(() => {
    if (!prete) return;
    window.clearTimeout(minuteur.current);
    setPhase("clair");
  }, [prete]);

  return { aller, noir: phase !== "clair", duree };
}
